/**
 * IFTimerTab Component - Smart Timer (Smart Plaite+ Feature)
 * 
 * A utility tab for tracking meal timing and fasting windows.
 * Now includes eating schedule settings for calorie window customization.
 * Presets: 16:8, 18:6, 20:4, OMAD, Custom
 * 
 * MIQ Integration (Light):
 * - Smart Timer sessions contribute as "Timing Context" signal to MIQ confidence
 * - No scoring math changes
 */

import React, { useState, useEffect } from 'react';
import { Brain, Clock, Timer, List, Zap, Play, Pause, Settings as SettingsIcon, Info } from 'lucide-react';
import { brand } from '../../styles/brand';

const { colors, gradients, typography, radius } = brand;

// Note: Button styles are in App.css to prevent flash on mount (.miq-rhythm-tab-btn, .miq-rhythm-days-btn)

// Shift worker tips configuration
const SHIFT_TIPS = {
  night_shifts: {
    icon: '🌙',
    label: 'Night Shift',
    color: '#6366f1',
    tips: [
      'Try setting your calorie reset to when your shift starts (e.g., 6 PM or 7 PM)',
      'Front-load protein before your shift for sustained energy',
      'Avoid heavy meals 2-3 hours before sleeping',
      'Stay hydrated throughout your shift - dehydration affects metabolism'
    ]
  },
  rotating_shifts: {
    icon: '🔄',
    label: 'Rotating Schedule',
    color: '#f59e0b',
    tips: [
      'Use "Custom Schedule" to adjust your reset time when shifts change',
      'Keep meal sizes consistent even when shift times vary',
      'Prep meals in advance for unpredictable days',
      'Log meals consistently - MIQ adapts to YOUR patterns'
    ]
  },
  emergency_oncall: {
    icon: '🚨',
    label: 'Emergency / On-Call',
    color: '#ef4444',
    tips: [
      'Keep healthy grab-and-go options ready for sudden calls',
      'Protein bars/shakes are great for unpredictable schedules',
      'Set a reset time for when you typically START your day',
      'Don\'t stress about timing perfection - consistency over time matters more'
    ]
  },
  irregular: {
    icon: '📅',
    label: 'Irregular Schedule',
    color: '#8b5cf6',
    tips: [
      'Pick a reset time that works for MOST of your days',
      'Focus on hitting daily targets rather than meal timing',
      'Use the Custom Schedule option for maximum flexibility',
      'Log meals whenever you eat - MIQ learns your unique patterns'
    ]
  }
};

const IFTimerTab = ({
  userId,
  aiNutritionGoals,
  BACKEND_URL,
  workScheduleType,
  onSettingsSaved,
  mealHistory,
  mealHistoryLoading,
  onLoadMealHistory,
  dailySummaries,
  initialTab,
  onInitialTabConsumed,
}) => {
  // Re-expose as props object for the small useEffect below without rewriting the massive body
  const props = { initialTab, onInitialTabConsumed };
  // Local state for fasting timer
  const [fastingState, setFastingState] = useState(() => {
    const saved = localStorage.getItem(`miq_fasting_${userId}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { isFasting: false, startTime: null, preset: '16' };
      }
    }
    return { isFasting: false, startTime: null, preset: '16' };
  });
  
  // Eating schedule settings state - loaded from server
  const [scheduleSettings, setScheduleSettings] = useState({
    eating_schedule_type: 'standard',
    if_preference: '16_8',
    eating_window_start: '12:00',
    custom_reset_time: '06:00'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Tab state: 'timer', 'custom', 'history', or 'activity'
  const [activeTab, setActiveTab] = useState(props.initialTab || 'timer');
  
  // Consume one-shot deep-link from parent (Smart Hub "Past 7 Days" CTA)
  useEffect(() => {
    if (props.initialTab) {
      setActiveTab(props.initialTab);
      props.onInitialTabConsumed?.();
    }
  }, [props.initialTab]); // eslint-disable-line
  
  // Custom timer state
  const [customTimerHours, setCustomTimerHours] = useState(0);
  const [customTimerMinutes, setCustomTimerMinutes] = useState(30);
  const [customTimerRunning, setCustomTimerRunning] = useState(false);
  const [customTimerEndTime, setCustomTimerEndTime] = useState(null);
  const [customTimerRemaining, setCustomTimerRemaining] = useState(0);
  const [customTimerMode, setCustomTimerMode] = useState('countdown'); // 'countdown' or 'stopwatch'
  const [stopwatchElapsed, setStopwatchElapsed] = useState(0);
  const [stopwatchStartTime, setStopwatchStartTime] = useState(null);
  
  // History days filter
  const [historyDays, setHistoryDays] = useState(7);
  
  // Activity history state
  const [activityHistory, setActivityHistory] = useState([]);
  const [activityHistoryLoading, setActivityHistoryLoading] = useState(false);
  const [activityDays, setActivityDays] = useState(7);
  
  const [elapsedTime, setElapsedTime] = useState(0);
  const [completedFasts, setCompletedFasts] = useState(() => {
    const saved = localStorage.getItem(`miq_fasts_completed_${userId}`);
    return saved ? parseInt(saved, 10) : 0;
  });
  
  // Get shift tips if applicable
  const shiftTips = workScheduleType && workScheduleType !== 'standard_day' 
    ? SHIFT_TIPS[workScheduleType] 
    : null;
  
  // Store callback in ref to avoid dependency issues
  const loadHistoryRef = React.useRef(onLoadMealHistory);
  loadHistoryRef.current = onLoadMealHistory;
  
  // Load meal history when switching to history tab or changing days filter
  useEffect(() => {
    if (activeTab === 'history' && loadHistoryRef.current) {
      loadHistoryRef.current(historyDays);
    }
  }, [activeTab, historyDays]);
  
  // Load activity history when switching to activity tab or changing days filter
  useEffect(() => {
    const loadActivityHistory = async () => {
      if (!userId || !BACKEND_URL) return;
      setActivityHistoryLoading(true);
      try {
        const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
        const res = await fetch(`${BACKEND_URL}/api/exercises/history/${userId}?days=${activityDays}&tz_offset=${tzOffset}`);
        if (res.ok) {
          const data = await res.json();
          setActivityHistory(data.daily_totals || []);
        }
      } catch (err) {
        console.error('Failed to load activity history:', err);
      } finally {
        setActivityHistoryLoading(false);
      }
    };
    
    if (activeTab === 'activity') {
      loadActivityHistory();
    }
  }, [activeTab, activityDays, userId, BACKEND_URL]);
  
  // Load eating schedule settings from server
  useEffect(() => {
    const loadSettings = async () => {
      if (!userId || !BACKEND_URL) return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${userId}`);
        if (res.ok) {
          const data = await res.json();
          const intake = data.intake_data || {};
          setScheduleSettings({
            eating_schedule_type: intake.eating_schedule_type || 'standard',
            if_preference: intake.if_preference || '16_8',
            eating_window_start: intake.eating_window_start || '12:00',
            custom_reset_time: intake.custom_reset_time || '06:00'
          });
        }
      } catch (err) {
        console.error('Failed to load eating schedule settings:', err);
      }
    };
    loadSettings();
  }, [userId, BACKEND_URL]);
  
  // Save eating schedule settings to server
  const saveScheduleSettings = async () => {
    if (!userId || !BACKEND_URL) return;
    setSavingSettings(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${userId}/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleSettings)
      });
      if (res.ok) {
        setShowSettings(false);
        // Refresh the parent's aiNutritionGoals data so Settings page shows updated value
        if (onSettingsSaved) {
          onSettingsSaved();
        }
      }
    } catch (err) {
      console.error('Failed to save eating schedule settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };
  
  // Get IF schedule from SmartGoals (if set)
  const ifSchedule = aiNutritionGoals?.nutrition_goals?.if_schedule;
  const defaultPreset = ifSchedule?.type?.replace('_', '') || '16';
  
  // Preset options with durations in hours
  const presets = [
    { value: '12', label: '12:12', hours: 12, description: '12hr fast, 12hr eating' },
    { value: '14', label: '14:10', hours: 14, description: '14hr fast, 10hr eating' },
    { value: '16', label: '16:8', hours: 16, description: '16hr fast, 8hr eating (Popular)' },
    { value: '18', label: '18:6', hours: 18, description: '18hr fast, 6hr eating' },
    { value: '20', label: 'OMAD', hours: 20, description: '20hr fast, 4hr eat' }
  ];
  
  // Timer effect
  useEffect(() => {
    let interval;
    if (fastingState.isFasting && fastingState.startTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - fastingState.startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [fastingState.isFasting, fastingState.startTime]);
  
  // Save fasting state to localStorage
  useEffect(() => {
    if (userId) {
      localStorage.setItem(`miq_fasting_${userId}`, JSON.stringify(fastingState));
    }
  }, [fastingState, userId]);
  
  // Custom countdown timer effect
  useEffect(() => {
    let interval;
    if (customTimerRunning && customTimerMode === 'countdown' && customTimerEndTime) {
      interval = setInterval(() => {
        const remaining = Math.max(0, Math.floor((customTimerEndTime - Date.now()) / 1000));
        setCustomTimerRemaining(remaining);
        
        // Timer complete
        if (remaining === 0) {
          setCustomTimerRunning(false);
          // Play notification sound (if browser supports it)
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Timer Complete!', { body: 'Your custom timer has finished.' });
          }
          // Vibrate if supported
          if (navigator.vibrate) {
            navigator.vibrate([200, 100, 200, 100, 200]);
          }
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [customTimerRunning, customTimerMode, customTimerEndTime]);
  
  // Stopwatch effect
  useEffect(() => {
    let interval;
    if (customTimerRunning && customTimerMode === 'stopwatch' && stopwatchStartTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - stopwatchStartTime) / 1000);
        setStopwatchElapsed(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [customTimerRunning, customTimerMode, stopwatchStartTime]);
  
  // Start custom countdown timer
  const startCustomTimer = () => {
    const totalSeconds = (customTimerHours * 3600) + (customTimerMinutes * 60);
    if (totalSeconds > 0) {
      setCustomTimerEndTime(Date.now() + (totalSeconds * 1000));
      setCustomTimerRemaining(totalSeconds);
      setCustomTimerRunning(true);
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  };
  
  // Start stopwatch
  const startStopwatch = () => {
    setStopwatchStartTime(Date.now());
    setStopwatchElapsed(0);
    setCustomTimerRunning(true);
  };
  
  // Stop custom timer or stopwatch
  const stopCustomTimer = () => {
    setCustomTimerRunning(false);
    setCustomTimerEndTime(null);
    setStopwatchStartTime(null);
  };
  
  // Reset custom timer
  const resetCustomTimer = () => {
    setCustomTimerRunning(false);
    setCustomTimerEndTime(null);
    setCustomTimerRemaining(0);
    setStopwatchElapsed(0);
    setStopwatchStartTime(null);
  };
  
  // Format time as HH:MM:SS
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  
  // Get target duration in seconds
  const currentPreset = fastingState.preset || defaultPreset;
  const targetSeconds = parseInt(currentPreset, 10) * 3600;
  const progressPercent = Math.min((elapsedTime / targetSeconds) * 100, 100);
  const remainingSeconds = Math.max(targetSeconds - elapsedTime, 0);
  const fastComplete = elapsedTime >= targetSeconds;
  
  // Start fast
  const startFast = () => {
    setFastingState({
      ...fastingState,
      isFasting: true,
      startTime: Date.now()
    });
  };
  
  // End fast
  const endFast = () => {
    if (fastComplete) {
      const newCount = completedFasts + 1;
      setCompletedFasts(newCount);
      localStorage.setItem(`miq_fasts_completed_${userId}`, newCount.toString());
    }
    setFastingState({
      ...fastingState,
      isFasting: false,
      startTime: null
    });
    setElapsedTime(0);
  };
  
  // MIQ Timing Context contribution (light integration)
  const timingContextContribution = Math.min(completedFasts * 0.5, 5);
  
  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '600px', 
      margin: '0 auto',
      paddingBottom: '100px',
      background: 'var(--bg-page, #0d0d14)',
      minHeight: '100vh'
    }}>
      {/* Header with Tab Switcher */}
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <h1 style={{
          margin: '0 0 8px 0',
          fontSize: '22px',
          fontWeight: '300',
          fontFamily: typography.headingFont,
          letterSpacing: '-0.02em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          color: colors.text,
        }}>
          <Brain size={20} style={{ color: colors.accent }} strokeWidth={1.5} />
          MetaBolicIQ Rhythm
          <span style={{
            fontSize: '9px',
            background: colors.freeDim,
            color: colors.free,
            border: `1px solid ${colors.freeBorder}`,
            padding: '3px 8px',
            borderRadius: radius.pill,
            fontWeight: '600',
            fontFamily: typography.headingFont,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}>
            Smart+
          </span>
        </h1>
        
        {/* Subtitle with feature categories */}
        <p style={{
          margin: '0 0 20px 0',
          fontSize: '10px',
          color: colors.mutedText,
          letterSpacing: '0.2em',
          fontFamily: typography.headingFont,
          fontWeight: '600',
          textTransform: 'uppercase',
        }}>
          Meal Timing · Fasting · Shift Schedules · Eating Patterns
        </p>
        
        {/* Tab Switcher */}
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('timer')}
            className={`miq-rhythm-tab-btn ${activeTab === 'timer' ? 'active-timer' : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Clock size={13} />
            Fasting Timer
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`miq-rhythm-tab-btn ${activeTab === 'custom' ? 'active-timer' : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Timer size={13} />
            Custom Timer
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`miq-rhythm-tab-btn ${activeTab === 'history' ? 'active-history' : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <List size={13} />
            Meal Timeline
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`miq-rhythm-tab-btn ${activeTab === 'activity' ? 'active-timer' : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Zap size={13} />
            Activity Log
          </button>
        </div>
        
        {activeTab === 'timer' && (
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: 'var(--text-secondary)',
            lineHeight: '1.5'
          }}>
            Track your eating window and fasting duration
          </p>
        )}
        {activeTab === 'custom' && (
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: 'var(--text-secondary)',
            lineHeight: '1.5'
          }}>
            Set a custom countdown or stopwatch
          </p>
        )}
        {activeTab === 'history' && (
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: 'var(--text-secondary)',
            lineHeight: '1.5'
          }}>
            View your logged meals timeline
          </p>
        )}
        {activeTab === 'activity' && (
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: 'var(--text-secondary)',
            lineHeight: '1.5'
          }}>
            View your exercise and activity history
          </p>
        )}
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          CUSTOM TIMER TAB
          ═══════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'custom' && (
        <div style={{
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '16px',
          padding: '24px 20px',
          border: '1px solid rgba(255,255,255,0.06)'
        }}>
          {/* Mode Selector */}
          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <button
              onClick={() => { setCustomTimerMode('countdown'); resetCustomTimer(); }}
              style={{
                padding: '10px 20px',
                background: customTimerMode === 'countdown' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : 'rgba(255,255,255,0.05)',
                border: customTimerMode === 'countdown' ? 'none' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: '20px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ⏳ Countdown
            </button>
            <button
              onClick={() => { setCustomTimerMode('stopwatch'); resetCustomTimer(); }}
              style={{
                padding: '10px 20px',
                background: customTimerMode === 'stopwatch' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : 'rgba(255,255,255,0.05)',
                border: customTimerMode === 'stopwatch' ? 'none' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: '20px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ⏱️ Stopwatch
            </button>
          </div>
          
          {/* Countdown Timer Setup */}
          {customTimerMode === 'countdown' && !customTimerRunning && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                Set timer duration
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Hours</label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={customTimerHours}
                    onChange={(e) => setCustomTimerHours(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
                    style={{
                      width: '70px',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '20px',
                      fontWeight: '700',
                      textAlign: 'center'
                    }}
                  />
                </div>
                <span style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-secondary)', marginTop: '16px' }}>:</span>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Minutes</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={customTimerMinutes}
                    onChange={(e) => setCustomTimerMinutes(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                    style={{
                      width: '70px',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '20px',
                      fontWeight: '700',
                      textAlign: 'center'
                    }}
                  />
                </div>
              </div>
              
              {/* Quick presets */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '24px', flexWrap: 'wrap' }}>
                {[
                  { label: '5 min', h: 0, m: 5 },
                  { label: '15 min', h: 0, m: 15 },
                  { label: '30 min', h: 0, m: 30 },
                  { label: '1 hr', h: 1, m: 0 },
                  { label: '2 hr', h: 2, m: 0 }
                ].map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => { setCustomTimerHours(preset.h); setCustomTimerMinutes(preset.m); }}
                    style={{
                      padding: '8px 14px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '16px',
                      color: 'var(--text-secondary)',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              
              <button
                onClick={startCustomTimer}
                disabled={customTimerHours === 0 && customTimerMinutes === 0}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: (customTimerHours > 0 || customTimerMinutes > 0) 
                    ? gradients.primary 
                    : 'rgba(255,255,255,0.08)',
                  border: 'none',
                  borderRadius: radius.md,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: typography.headingFont,
                  letterSpacing: '0.02em',
                  cursor: (customTimerHours > 0 || customTimerMinutes > 0) ? 'pointer' : 'not-allowed',
                  opacity: (customTimerHours > 0 || customTimerMinutes > 0) ? 1 : 0.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: (customTimerHours > 0 || customTimerMinutes > 0)
                    ? `0 4px 24px ${colors.accentGlow}`
                    : 'none',
                }}
              >
                <Play size={15} fill="currentColor" />
                Start Timer
              </button>
            </div>
          )}
          
          {/* Countdown Running Display */}
          {customTimerMode === 'countdown' && customTimerRunning && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '52px',
                fontWeight: '300',
                fontFamily: typography.monoFont,
                letterSpacing: '-0.04em',
                color: customTimerRemaining <= 60 ? colors.error : customTimerRemaining <= 300 ? colors.warning : colors.free,
                marginBottom: '24px',
                textShadow: `0 0 20px ${customTimerRemaining <= 60 ? 'rgba(239, 68, 68, 0.3)' : customTimerRemaining <= 300 ? 'rgba(245, 158, 11, 0.3)' : 'rgba(0, 229, 160, 0.3)'}`,
              }}>
                {formatTime(customTimerRemaining)}
              </div>
              
              {customTimerRemaining === 0 && (
                <div style={{
                  padding: '16px',
                  background: colors.freeDim,
                  borderRadius: radius.lg,
                  marginBottom: '24px',
                  border: `1px solid ${colors.freeBorder}`
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    fontFamily: typography.headingFont,
                    fontWeight: '600',
                    color: colors.free,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                  }}>
                    Timer Complete
                  </p>
                </div>
              )}
              
              <button
                onClick={resetCustomTimer}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  border: 'none',
                  borderRadius: radius.md,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: typography.headingFont,
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                Reset
              </button>
            </div>
          )}
          
          {/* Stopwatch Display */}
          {customTimerMode === 'stopwatch' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '52px',
                fontWeight: '300',
                fontFamily: typography.monoFont,
                letterSpacing: '-0.04em',
                color: colors.accent,
                marginBottom: '24px',
                textShadow: `0 0 20px ${colors.accentGlow}`
              }}>
                {formatTime(stopwatchElapsed)}
              </div>
              
              {!customTimerRunning ? (
                <button
                  onClick={startStopwatch}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: gradients.primary,
                    border: 'none',
                    borderRadius: radius.md,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: typography.headingFont,
                    letterSpacing: '0.02em',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: `0 4px 24px ${colors.accentGlow}`,
                  }}
                >
                  <Play size={15} fill="currentColor" />
                  Start
                </button>
              ) : (
                <button
                  onClick={stopCustomTimer}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    border: 'none',
                    borderRadius: radius.md,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: typography.headingFont,
                    letterSpacing: '0.02em',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <Pause size={15} fill="currentColor" />
                  Pause
                </button>
              )}
              
              {stopwatchElapsed > 0 && (
                <button
                  onClick={resetCustomTimer}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  🔄 Reset
                </button>
              )}
            </div>
          )}
          
          {/* Close Button */}
          <button
            onClick={() => setActiveTab('timer')}
            style={{
              width: '100%',
              padding: '14px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              color: 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              marginTop: '24px'
            }}
          >
            ← Back to Fasting Timer
          </button>
        </div>
      )}
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          MEAL HISTORY TAB
          ═══════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'history' && (
        <MealHistorySection 
          mealHistory={mealHistory}
          mealHistoryLoading={mealHistoryLoading}
          historyDays={historyDays}
          setHistoryDays={setHistoryDays}
          dailySummaries={dailySummaries}
        />
      )}
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          ACTIVITY HISTORY TAB
          ═══════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'activity' && (
        <ActivityHistorySection 
          activityHistory={activityHistory}
          activityHistoryLoading={activityHistoryLoading}
          activityDays={activityDays}
          setActivityDays={setActivityDays}
        />
      )}
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SMART TIMER TAB
          ═══════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'timer' && (
      <>
      {/* MIQ Context Helper */}
      <div style={{
        background: colors.accentDim,
        border: `1px solid ${colors.accentBorder}`,
        borderRadius: radius.lg,
        padding: '12px 14px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}>
        <Brain size={14} style={{ color: colors.accent, flexShrink: 0, marginTop: '1px' }} />
        <p style={{
          margin: 0,
          fontSize: '12px',
          color: colors.textSecondary,
          lineHeight: '1.55',
          fontFamily: typography.fontFamily,
        }}>
          Fasting patterns help MetaBolicIQ understand your metabolic rhythm over time.
        </p>
      </div>
      
      {/* Preset Selector Card */}
      <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: '10px',
          fontFamily: typography.headingFont,
          fontWeight: '600',
          color: colors.textSecondary,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
        }}>
          Select Fasting Window
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {presets.map(preset => (
            <button
              key={preset.value}
              onClick={() => !fastingState.isFasting && setFastingState({ ...fastingState, preset: preset.value })}
              disabled={fastingState.isFasting}
              style={{
                padding: '14px 16px',
                border: currentPreset === preset.value
                  ? `1px solid ${colors.accentBorder}`
                  : `1px solid ${colors.stroke}`,
                borderRadius: radius.md,
                background: currentPreset === preset.value
                  ? gradients.primary
                  : 'rgba(255,255,255,0.04)',
                color: currentPreset === preset.value ? 'white' : colors.text,
                fontFamily: typography.headingFont,
                fontWeight: '500',
                fontSize: '14px',
                cursor: fastingState.isFasting ? 'not-allowed' : 'pointer',
                opacity: fastingState.isFasting ? 0.6 : 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left',
                minHeight: '48px',
                flexWrap: 'wrap',
                gap: '4px',
                boxShadow: currentPreset === preset.value
                  ? '0 0 20px rgba(124, 92, 252, 0.25)'
                  : 'none',
                transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontWeight: '600', minWidth: '50px' }}>{preset.label}</span>
              <span style={{ 
                fontSize: '12px', 
                opacity: currentPreset === preset.value ? 1 : 0.7,
                textAlign: 'right',
                flex: 1
              }}>
                {preset.description}
              </span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Timer Display Card */}
      <div className="card" style={{ marginBottom: '16px', padding: '20px' }}>
        {fastingState.isFasting ? (
          <>
            {/* Active Fast */}
            <div style={{
              background: fastComplete 
                ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 239, 125, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              borderRadius: '16px',
              padding: '24px 16px',
              textAlign: 'center',
              marginBottom: '16px'
            }}>
              <p style={{ 
                margin: '0 0 8px 0', 
                fontSize: '14px', 
                color: 'var(--text-secondary)',
                fontWeight: '500'
              }}>
                {fastComplete ? '🎉 Fast Complete!' : '🔥 Currently Fasting'}
              </p>
              
              {/* Big Timer - Responsive */}
              <div style={{ 
                fontSize: 'clamp(36px, 10vw, 48px)', 
                fontWeight: '700', 
                color: fastComplete ? '#4caf50' : '#667eea',
                fontFamily: 'monospace',
                marginBottom: '8px',
                letterSpacing: '1px',
                lineHeight: 1.2
              }}>
                {formatTime(elapsedTime)}
              </div>
              
              {!fastComplete && (
                <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {formatTime(remainingSeconds)} left to {currentPreset}h goal
                </p>
              )}
              
              {/* Progress Bar */}
              <div style={{ 
                width: '100%', 
                height: '10px', 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: '5px', 
                overflow: 'hidden',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: `${progressPercent}%`,
                  height: '100%',
                  background: fastComplete 
                    ? 'linear-gradient(90deg, #4caf50, #38ef7d)' 
                    : 'linear-gradient(90deg, #667eea, #764ba2)',
                  borderRadius: '5px',
                  transition: 'width 1s linear'
                }} />
              </div>
              
              <button
                onClick={endFast}
                style={{
                  padding: '14px 40px',
                  border: 'none',
                  borderRadius: '12px',
                  background: fastComplete ? '#4caf50' : '#f44336',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  minHeight: '48px'
                }}
              >
                {fastComplete ? '✅ Complete Fast' : '⏹️ End Fast Early'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Not Fasting - Show Start Button */}
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ 
                margin: '0 0 20px 0', 
                fontSize: '14px', 
                color: 'var(--text-secondary)' 
              }}>
                Ready to start your {currentPreset}:{ 24 - parseInt(currentPreset, 10)} fast?
              </p>
              <button
                onClick={startFast}
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  padding: '18px 32px',
                  border: 'none',
                  borderRadius: radius.lg,
                  background: gradients.primary,
                  color: 'white',
                  fontFamily: typography.headingFont,
                  fontWeight: '600',
                  fontSize: '15px',
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  boxShadow: '0 4px 24px rgba(124, 92, 252, 0.35)',
                  minHeight: '56px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
              >
                <Play size={16} fill="currentColor" />
                Start {currentPreset}h Fast
              </button>
            </div>
          </>
        )}
      </div>
      
      {/* Eating Schedule Settings Card */}
      <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
        <div 
          onClick={() => setShowSettings(!showSettings)}
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            cursor: 'pointer'
          }}
        >
          <div>
            <h3 style={{
              margin: '0 0 4px 0',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: typography.headingFont,
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              letterSpacing: '-0.005em',
            }}>
              <SettingsIcon size={14} style={{ color: colors.accent }} />
              Eating Schedule Settings
            </h3>
            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)' }}>
              {scheduleSettings.eating_schedule_type === 'intermittent_fasting' 
                ? `IF ${scheduleSettings.if_preference?.replace('_', ':')} • Eating starts at ${scheduleSettings.eating_window_start}`
                : scheduleSettings.eating_schedule_type === 'custom'
                  ? `Custom • Day resets at ${scheduleSettings.custom_reset_time}`
                  : 'Standard • Day resets at midnight'}
            </p>
          </div>
          <span style={{ 
            fontSize: '16px',
            transition: 'transform 0.2s',
            transform: showSettings ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
        
        {showSettings && (
          <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px' }}>
            {/* Schedule Type Selection */}
            <p style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
              How do you want to track your day?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {[
                { value: 'standard', label: '🌙 Standard', desc: 'Day resets at midnight' },
                { value: 'intermittent_fasting', label: '⏰ Intermittent Fasting', desc: 'Day resets when eating window ends' },
                { value: 'custom', label: '🔧 Custom Schedule', desc: 'For shift workers or irregular hours' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setScheduleSettings({ ...scheduleSettings, eating_schedule_type: opt.value })}
                  style={{
                    padding: '12px 14px',
                    border: 'none',
                    borderRadius: '10px',
                    background: scheduleSettings.eating_schedule_type === opt.value
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'rgba(255,255,255,0.05)',
                    color: scheduleSettings.eating_schedule_type === opt.value ? 'white' : 'var(--text-primary)',
                    fontWeight: '500',
                    fontSize: '13px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <span style={{ fontWeight: '600' }}>{opt.label}</span>
                  <span style={{ 
                    display: 'block', 
                    fontSize: '11px', 
                    opacity: scheduleSettings.eating_schedule_type === opt.value ? 1 : 0.7,
                    marginTop: '2px'
                  }}>
                    {opt.desc}
                  </span>
                </button>
              ))}
            </div>
            
            {/* IF Protocol Options */}
            {scheduleSettings.eating_schedule_type === 'intermittent_fasting' && (
              <>
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Fasting Protocol
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {['12_12', '14_10', '16_8', '18_6', '20_4'].map(protocol => (
                    <button
                      key={protocol}
                      onClick={() => setScheduleSettings({ ...scheduleSettings, if_preference: protocol })}
                      style={{
                        padding: '8px 14px',
                        border: 'none',
                        borderRadius: '8px',
                        background: scheduleSettings.if_preference === protocol
                          ? 'rgba(56, 239, 125, 0.2)'
                          : 'rgba(255,255,255,0.05)',
                        color: scheduleSettings.if_preference === protocol ? '#38ef7d' : 'var(--text-secondary)',
                        fontWeight: '600',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      {protocol.replace('_', ':')}
                    </button>
                  ))}
                </div>
                
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Eating Window Starts At
                </p>
                
                {/* Quick presets */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                  {['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'].map(time => {
                    const hour = parseInt(time.split(':')[0]);
                    const label = hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour > 12 ? `${hour-12} PM` : `${hour} AM`;
                    return (
                      <button
                        key={time}
                        onClick={() => setScheduleSettings({ ...scheduleSettings, eating_window_start: time })}
                        style={{
                          padding: '6px 10px',
                          border: 'none',
                          borderRadius: '8px',
                          background: scheduleSettings.eating_window_start === time
                            ? 'rgba(56, 239, 125, 0.2)'
                            : 'rgba(255,255,255,0.05)',
                          color: scheduleSettings.eating_window_start === time ? '#38ef7d' : 'var(--text-secondary)',
                          fontWeight: '500',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                
                {/* Custom time picker */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  padding: '10px 12px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Or pick exact time:</span>
                  <input
                    type="time"
                    value={scheduleSettings.eating_window_start || '12:00'}
                    onChange={(e) => setScheduleSettings({ ...scheduleSettings, eating_window_start: e.target.value })}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid rgba(56, 239, 125, 0.3)',
                      borderRadius: '8px',
                      background: 'rgba(56, 239, 125, 0.1)',
                      color: '#38ef7d',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </>
            )}
            
            {/* Custom Reset Time Options */}
            {scheduleSettings.eating_schedule_type === 'custom' && (
              <>
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Day Resets At
                </p>
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Common shift presets:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                    {[
                      { time: '00:00', label: '🌙', desc: 'Midnight' },
                      { time: '04:00', label: '🌄', desc: '4 AM' },
                      { time: '05:00', label: '🌅', desc: '5 AM' },
                      { time: '06:00', label: '☀️', desc: '6 AM' },
                      { time: '07:00', label: '🌤️', desc: '7 AM' },
                      { time: '14:00', label: '🌞', desc: '2 PM' },
                      { time: '15:00', label: '🌇', desc: '3 PM' },
                      { time: '18:00', label: '🌆', desc: '6 PM' },
                      { time: '19:00', label: '🌃', desc: '7 PM' },
                      { time: '22:00', label: '🌑', desc: '10 PM' }
                    ].map(preset => (
                      <button
                        key={preset.time}
                        onClick={() => setScheduleSettings({ ...scheduleSettings, custom_reset_time: preset.time })}
                        style={{
                          padding: '8px 10px',
                          border: 'none',
                          borderRadius: '8px',
                          background: scheduleSettings.custom_reset_time === preset.time
                            ? 'rgba(255, 159, 67, 0.2)'
                            : 'rgba(255,255,255,0.05)',
                          color: scheduleSettings.custom_reset_time === preset.time ? '#ff9f43' : 'var(--text-secondary)',
                          fontWeight: '500',
                          fontSize: '11px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          minWidth: '55px'
                        }}
                      >
                        <span style={{ display: 'block', fontSize: '14px' }}>{preset.label}</span>
                        <span style={{ display: 'block', fontWeight: '600', fontSize: '10px' }}>{preset.desc}</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* Custom time picker */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    padding: '10px 12px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px'
                  }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Or pick exact time:</span>
                    <input
                      type="time"
                      value={scheduleSettings.custom_reset_time || '06:00'}
                      onChange={(e) => setScheduleSettings({ ...scheduleSettings, custom_reset_time: e.target.value })}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid rgba(255, 159, 67, 0.3)',
                        borderRadius: '8px',
                        background: 'rgba(255, 159, 67, 0.1)',
                        color: '#ff9f43',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Save Button */}
            <button
              onClick={saveScheduleSettings}
              disabled={savingSettings}
              style={{
                width: '100%',
                padding: '14px',
                border: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                cursor: savingSettings ? 'not-allowed' : 'pointer',
                opacity: savingSettings ? 0.7 : 1
              }}
            >
              {savingSettings ? 'Saving...' : '💾 Save Settings'}
            </button>
          </div>
        )}
      </div>
      
      {/* Shift Worker Tips Card - shows only for non-standard schedules */}
      {shiftTips && (
        <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '12px'
          }}>
            <span style={{ fontSize: '18px' }}>{shiftTips.icon}</span>
            <h3 style={{ 
              margin: 0, 
              fontSize: '14px', 
              fontWeight: '600', 
              color: shiftTips.color 
            }}>
              {shiftTips.label} Tips
            </h3>
          </div>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '10px' 
          }}>
            {shiftTips.tips.map((tip, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '10px 12px',
                  background: `${shiftTips.color}10`,
                  borderRadius: '8px',
                  borderLeft: `3px solid ${shiftTips.color}`
                }}
              >
                <span style={{ fontSize: '12px', flexShrink: 0 }}>💡</span>
                <span style={{ 
                  fontSize: '12px', 
                  color: 'var(--text-primary)',
                  lineHeight: '1.4'
                }}>
                  {tip}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Stats Card */}
      <div className="card" style={{ padding: '16px' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>
          Your Fasting Stats
        </h3>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          padding: '12px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '10px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#667eea' }}>
              {completedFasts}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              Fasts Completed
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#4caf50' }}>
              {completedFasts > 0 ? Math.min(Math.round(completedFasts / 7 * 100), 100) : 0}%
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              Weekly Consistency
            </div>
          </div>
          {timingContextContribution > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#ff9800' }}>
                +{timingContextContribution.toFixed(1)}%
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                MIQ Context
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Info Footer */}
      <p style={{ 
        marginTop: '20px', 
        fontSize: '11px', 
        color: 'var(--text-secondary)', 
        textAlign: 'center',
        lineHeight: '1.5'
      }}>
        Tracking your eating window provides timing context that helps MIQ understand your metabolic rhythm.
      </p>
      </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MEAL HISTORY SECTION COMPONENT
// Shows logged meals grouped by day with timestamps
// ═══════════════════════════════════════════════════════════════════════════════
// Small stat pill used inside expanded meal detail rows. Keeps the row
// nutrition-dense without cluttering the default compact view.
const DetailStat = ({ label, value, color }) => (
  <div style={{
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.03)',
    border: `1px solid ${colors.stroke}`,
    borderRadius: radius.sm || '6px',
    minWidth: 0,
  }}>
    <div style={{
      fontSize: '9px',
      color: colors.mutedText,
      fontFamily: typography.headingFont,
      fontWeight: '600',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      marginBottom: '2px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '13px',
      color,
      fontFamily: typography.monoFont,
      fontWeight: '500',
      letterSpacing: '-0.01em',
    }}>
      {value}
    </div>
  </div>
);

const MealHistorySection = ({ mealHistory, mealHistoryLoading, historyDays, setHistoryDays, dailySummaries }) => {
  // Set of expanded meal_ids — tap a meal row to reveal full macros + fiber + source tag
  const [expandedMealIds, setExpandedMealIds] = useState(new Set());
  const toggleExpanded = (id) => {
    setExpandedMealIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Group meals by date
  const groupMealsByDate = (meals) => {
    if (!meals || meals.length === 0) return {};
    
    const groups = {};
    
    // Get user's timezone offset for proper date conversion
    // Meals are stored with UTC timestamps, so we need to convert to local date
    const tzOffset = (typeof window !== 'undefined' && window.__userTimezoneOffset !== undefined) 
      ? window.__userTimezoneOffset 
      : Math.round(-new Date().getTimezoneOffset() / 60);
    
    meals.forEach(meal => {
      let date;
      
      if (meal.date) {
        // Use the explicit date field if available (already in user's local date)
        date = meal.date;
      } else if (meal.timestamp) {
        // Convert UTC timestamp to user's local date
        // This fixes the issue where meals logged late in the day (in user's timezone)
        // appear on the next day because the UTC date is different
        const utcDate = new Date(meal.timestamp);
        const localDate = new Date(utcDate.getTime() + (tzOffset * 60 * 60 * 1000));
        date = localDate.toISOString().split('T')[0];
      } else {
        date = 'Unknown';
      }
      
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(meal);
    });
    
    // Sort each day's meals by timestamp (newest first)
    Object.keys(groups).forEach(date => {
      groups[date].sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });
    });
    
    return groups;
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    
    // Use user's local "today" from the backend (accounts for timezone and reset time)
    // Falls back to browser's local date if not available
    const userLocalToday = window.__userLocalToday || new Date().toISOString().split('T')[0];
    
    // Debug logging for timezone issues
    if (process.env.NODE_ENV !== 'production') {
      console.log('[formatDate] dateStr:', dateStr, 'userLocalToday:', userLocalToday);
    }
    
    // Calculate "yesterday" relative to user's local today
    const todayDate = new Date(userLocalToday + 'T00:00:00');
    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterdayStr = yesterdayDate.toISOString().split('T')[0];
    
    if (dateStr === userLocalToday) {
      return 'Today';
    } else if (dateStr === yesterdayStr) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };
  
  const getSourceIcon = (source) => {
    switch(source) {
      case 'camera': return '📸';
      case 'upload': return '🖼️';
      case 'manual': return '✍️';
      case 'smart_meals': return '🍽️';
      case 'smart_swaps': return '🔄';
      case 'barcode': return '📊';
      default: return '🍴';
    }
  };
  
  const groupedMeals = groupMealsByDate(mealHistory);
  const sortedDates = Object.keys(groupedMeals).sort((a, b) => b.localeCompare(a));
  
  return (
    <div data-testid="meal-history-section">
      {/* Days Filter */}
      <div style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        marginBottom: '20px'
      }}>
        {[7, 14, 30].map(days => (
          <button
            key={days}
            onClick={() => setHistoryDays(days)}
            className={`miq-rhythm-days-btn ${historyDays === days ? 'active' : ''}`}
          >
            {days} days
          </button>
        ))}
      </div>
      
      {/* Content Container - Fixed background to prevent white flash */}
      <div style={{ 
        minHeight: '200px',
        background: 'var(--bg-page, #0d0d14)',
        borderRadius: '12px'
      }}>
        {/* Loading State */}
        {mealHistoryLoading && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
            Loading meal history...
          </div>
        )}
        
        {/* MIQ Timing Context Banner */}
        {!mealHistoryLoading && mealHistory && mealHistory.length > 0 && (
          <div style={{
            background: 'rgba(102, 126, 234, 0.08)',
            border: '1px solid rgba(102, 126, 234, 0.15)',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <span style={{ fontSize: '16px', flexShrink: 0 }}>🧠</span>
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-primary)', fontWeight: '600' }}>
                MIQ Timing Analysis Active
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                Your meal timestamps help MIQ understand your eating patterns for your personalized baseline report.
              </p>
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {!mealHistoryLoading && (!mealHistory || mealHistory.length === 0) && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '12px',
            border: '1px dashed rgba(255,255,255,0.1)'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🍽️</div>
            <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              No meals logged yet
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              Your meal timeline will appear here as you log meals
            </p>
          </div>
        )}
        
        {/* Meal Timeline by Day */}
        {!mealHistoryLoading && sortedDates.map(date => {
          // Prefer authoritative daily_summary (accurate after our Feb 23 hook
          // wiring); fall back to summed meal data if not yet computed.
          const summary = (dailySummaries || []).find(s => s.date === date);
          const dayMeals = groupedMeals[date];
          const dayCal = summary?.total_calories ?? dayMeals.reduce((sum, m) => sum + (m.calories || 0), 0);
          const dayProtein = summary?.total_protein ?? dayMeals.reduce((sum, m) => sum + (m.protein || 0), 0);
          const dayCarbs = summary?.total_carbs ?? dayMeals.reduce((sum, m) => sum + (m.carbs || 0), 0);
          const dayFat = summary?.total_fat ?? dayMeals.reduce((sum, m) => sum + (m.fat || 0), 0);
          return (
        <div key={date} className="meal-history-day" style={{ marginBottom: '24px' }}>
          {/* Day Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '8px',
            padding: '10px 14px',
            background: colors.accentDim,
            border: `1px solid ${colors.accentBorder}`,
            borderRadius: radius.md,
            backdropFilter: 'blur(12px)',
          }}>
            <span style={{
              fontSize: '10px',
              fontFamily: typography.headingFont,
              fontWeight: '600',
              color: colors.accent,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}>
              {formatDate(date)}
            </span>
            <span style={{
              fontSize: '11px',
              color: colors.textSecondary,
              marginLeft: 'auto',
              fontFamily: typography.fontFamily,
            }}>
              {dayMeals.length} meal{dayMeals.length > 1 ? 's' : ''}
            </span>
          </div>
          
          {/* Daily Totals Strip — uses daily_summaries (authoritative) */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 14px',
            marginBottom: '12px',
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${colors.stroke}`,
            borderRadius: radius.md,
            flexWrap: 'wrap',
          }}>
            <span style={{
              fontSize: '18px',
              fontWeight: '400',
              fontFamily: typography.monoFont,
              color: colors.text,
              letterSpacing: '-0.02em',
            }}>
              {Math.round(dayCal)}
              <span style={{ fontSize: '10px', opacity: 0.55, marginLeft: '3px', fontWeight: '400' }}>kcal</span>
            </span>
            <span style={{ color: colors.stroke, fontSize: '10px' }}>·</span>
            <span style={{ fontSize: '12px', fontFamily: typography.monoFont, color: colors.protein, fontWeight: '500' }}>
              P {Math.round(dayProtein)}<span style={{ opacity: 0.6, fontSize: '9px' }}>g</span>
            </span>
            <span style={{ fontSize: '12px', fontFamily: typography.monoFont, color: colors.carbs, fontWeight: '500' }}>
              C {Math.round(dayCarbs)}<span style={{ opacity: 0.6, fontSize: '9px' }}>g</span>
            </span>
            <span style={{ fontSize: '12px', fontFamily: typography.monoFont, color: colors.fat, fontWeight: '500' }}>
              F {Math.round(dayFat)}<span style={{ opacity: 0.6, fontSize: '9px' }}>g</span>
            </span>
          </div>
          
          {/* Meals for this day */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {groupedMeals[date].map((meal, idx) => {
              const mealKey = meal.meal_id || `${date}-${idx}`;
              const isExpanded = expandedMealIds.has(mealKey);
              const netCarbs = Math.max(0, (meal.carbs || 0) - (meal.fiber || 0));
              return (
              <div
                key={mealKey}
                className="meal-history-item"
                data-testid={`meal-history-item-${mealKey}`}
                onClick={() => toggleExpanded(mealKey)}
                style={{
                  padding: '12px 14px',
                  background: isExpanded ? 'rgba(124, 92, 252, 0.06)' : 'rgba(255,255,255,0.03)',
                  borderRadius: radius.md,
                  border: isExpanded
                    ? `1px solid ${colors.accentBorder}`
                    : `1px solid ${colors.stroke}`,
                  cursor: 'pointer',
                  transition: 'background 0.2s ease, border-color 0.2s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {/* Time Column - Enhanced for MIQ visibility */}
                <div style={{ 
                  minWidth: '70px',
                  textAlign: 'center',
                  padding: '6px 8px',
                  background: colors.accentDim,
                  border: `1px solid ${colors.accentBorder}`,
                  borderRadius: radius.sm || '8px',
                }}>
                  <div style={{ fontSize: '9px', color: colors.mutedText, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: typography.headingFont, fontWeight: '600' }}>
                    Logged
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: colors.accent, fontFamily: typography.monoFont, letterSpacing: '-0.01em' }}>
                    {formatTime(meal.timestamp)}
                  </div>
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>
                    {getSourceIcon(meal.source)}
                  </div>
                </div>
                
                {/* Meal Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: colors.text,
                    marginBottom: '6px',
                    fontFamily: typography.fontFamily,
                  }}>
                    <span style={{
                      flex: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {meal.food_items || meal.name || 'Unnamed meal'}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      color: colors.mutedText,
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      flexShrink: 0,
                    }}>
                      ▾
                    </span>
                  </div>
                  
                  {/* Compact Macros Row */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '12px',
                    fontSize: '12px',
                    fontFamily: typography.monoFont,
                    letterSpacing: '-0.01em',
                  }}>
                    <span style={{ color: colors.text, fontWeight: '500' }}>
                      {meal.calories || 0}<span style={{ fontSize: '9px', opacity: 0.55, marginLeft: '2px' }}>kcal</span>
                    </span>
                    <span style={{ color: colors.protein, fontWeight: '500' }}>
                      P {meal.protein || 0}<span style={{ fontSize: '9px', opacity: 0.6 }}>g</span>
                    </span>
                    <span style={{ color: colors.carbs, fontWeight: '500' }}>
                      C {meal.carbs || 0}<span style={{ fontSize: '9px', opacity: 0.6 }}>g</span>
                    </span>
                    <span style={{ color: colors.fat, fontWeight: '500' }}>
                      F {meal.fat || 0}<span style={{ fontSize: '9px', opacity: 0.6 }}>g</span>
                    </span>
                  </div>
                </div>
                </div>
                
                {/* Expanded Detail — fiber, net carbs, source, portion */}
                {isExpanded && (
                  <div
                    data-testid={`meal-detail-${mealKey}`}
                    style={{
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: `1px dashed ${colors.stroke}`,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
                      gap: '8px',
                    }}
                  >
                    <DetailStat label="Net Carbs" value={`${netCarbs.toFixed(1)}g`} color={colors.free} />
                    <DetailStat label="Fiber" value={`${meal.fiber || 0}g`} color={colors.carbs} />
                    {meal.sugar !== undefined && meal.sugar !== null && (
                      <DetailStat label="Sugar" value={`${meal.sugar}g`} color={colors.warning} />
                    )}
                    {meal.sodium !== undefined && meal.sodium !== null && (
                      <DetailStat label="Sodium" value={`${meal.sodium}mg`} color={colors.textSecondary} />
                    )}
                    {meal.portion_percent && meal.portion_percent !== 100 && (
                      <DetailStat label="Portion" value={`${meal.portion_percent}%`} color={colors.accent} />
                    )}
                    {meal.source && (
                      <DetailStat
                        label="Source"
                        value={String(meal.source).replace(/_/g, ' ')}
                        color={colors.textSecondary}
                      />
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
        );
      })}
      
        {/* Summary Footer */}
        {!mealHistoryLoading && mealHistory && mealHistory.length > 0 && (
          <div style={{
            marginTop: '20px',
            padding: '14px',
            background: 'rgba(102, 126, 234, 0.06)',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
              📊 Total: <strong style={{ color: 'var(--text-primary)' }}>{mealHistory.length} meals</strong> in the last {historyDays} days
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVITY HISTORY SECTION COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const ActivityHistorySection = ({ activityHistory, activityHistoryLoading, activityDays, setActivityDays }) => {
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const userLocalToday = window.__userLocalToday || new Date().toISOString().split('T')[0];
    
    const todayDate = new Date(userLocalToday + 'T00:00:00');
    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterdayStr = yesterdayDate.toISOString().split('T')[0];
    
    if (dateStr === userLocalToday) {
      return 'Today';
    } else if (dateStr === yesterdayStr) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };
  
  const getActivityIcon = (exerciseType) => {
    switch(exerciseType) {
      case 'steps': return '👟';
      case 'walking': return '🚶';
      case 'running': return '🏃';
      case 'cycling': return '🚴';
      case 'swimming': return '🏊';
      case 'strength': return '💪';
      case 'yoga': return '🧘';
      case 'cardio': return '❤️';
      default: return '🏋️';
    }
  };
  
  const sortedDays = [...activityHistory].sort((a, b) => b.date.localeCompare(a.date));
  
  return (
    <div data-testid="activity-history-section">
      {/* Days Filter */}
      <div style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        marginBottom: '20px'
      }}>
        {[7, 14, 30].map(days => (
          <button
            key={days}
            onClick={() => setActivityDays(days)}
            className={`miq-rhythm-days-btn ${activityDays === days ? 'active' : ''}`}
          >
            {days} days
          </button>
        ))}
      </div>
      
      {/* Content Container */}
      <div style={{ 
        minHeight: '200px',
        background: 'var(--bg-page, #0d0d14)',
        borderRadius: '12px'
      }}>
        {/* Loading State */}
        {activityHistoryLoading && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
            Loading activity history...
          </div>
        )}
        
        {/* Empty State */}
        {!activityHistoryLoading && (!activityHistory || activityHistory.length === 0) && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: 'var(--text-secondary)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }}>🏃</div>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '500' }}>
              No activities logged yet
            </p>
            <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
              Log activities and steps from the Home screen
            </p>
          </div>
        )}
        
        {/* Activity Days */}
        {!activityHistoryLoading && sortedDays.map(dayData => (
          <div key={dayData.date} style={{ marginBottom: '16px' }}>
            {/* Date Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              background: 'rgba(102, 126, 234, 0.08)',
              borderRadius: '8px',
              marginBottom: '8px'
            }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {formatDate(dayData.date)}
              </span>
              <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                {dayData.total_steps > 0 && (
                  <span>👟 {dayData.total_steps.toLocaleString()} steps</span>
                )}
                <span>🔥 {dayData.total_calories} cal</span>
              </div>
            </div>
            
            {/* Activities for this day */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {dayData.exercises.map((exercise, idx) => (
                <div 
                  key={exercise.exercise_id || idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>
                    {getActivityIcon(exercise.exercise_type)}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {exercise.exercise_type === 'steps' 
                        ? `${exercise.steps?.toLocaleString() || 0} steps`
                        : exercise.activity || exercise.exercise_type || 'Exercise'}
                    </div>
                    {exercise.duration && (
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {exercise.duration} min
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    fontWeight: '600',
                    color: '#f97316',
                    background: 'rgba(249, 115, 22, 0.1)',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}>
                    🔥 {exercise.calories_burned || 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        
        {/* Summary Footer */}
        {!activityHistoryLoading && activityHistory && activityHistory.length > 0 && (
          <div style={{
            marginTop: '20px',
            padding: '14px',
            background: 'rgba(249, 115, 22, 0.06)',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
              🏃 Total: <strong style={{ color: 'var(--text-primary)' }}>
                {activityHistory.reduce((sum, d) => sum + d.exercises.length, 0)} activities
              </strong> • <strong style={{ color: '#f97316' }}>
                {activityHistory.reduce((sum, d) => sum + d.total_calories, 0)} cal burned
              </strong> in the last {activityDays} days
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IFTimerTab;
